/**
 * Industry Selector Component
 *
 * Dropdown/Modal for selecting and switching between industries.
 * Provides visual feedback during industry switching.
 */

import { useState, useRef, useEffect } from 'react'
import {
  Factory,
  Droplets,
  Heart,
  Landmark,
  Zap,
  Truck,
  ChevronDown,
  Check,
  Loader2,
  Building2,
} from 'lucide-react'
import clsx from 'clsx'
import { useIndustry } from '../../contexts/IndustryContext'
import { IndustryId } from '../../config/industryTypes'
import { getIndustrySummaries } from '../../config/industries'

// =============================================================================
// ICON MAPPING
// =============================================================================

import type { LucideIcon } from 'lucide-react'

const industryIcons: Record<string, LucideIcon> = {
  Factory,
  Droplets,
  Heart,
  Landmark,
  Zap,
  Truck,
  Building2,
}

function getIndustryIcon(iconName: string) {
  return industryIcons[iconName] || Building2
}

// =============================================================================
// INDUSTRY SELECTOR COMPONENT
// =============================================================================

interface IndustrySelectorProps {
  variant?: 'dropdown' | 'cards' | 'minimal'
  showDescription?: boolean
  className?: string
  onSelect?: (industryId: IndustryId) => void
}

export function IndustrySelector({
  variant = 'dropdown',
  showDescription = true,
  className,
  onSelect,
}: IndustrySelectorProps) {
  const { industry, industryId, setIndustry, isLoading } = useIndustry()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const industries = getIndustrySummaries()
  const CurrentIcon = getIndustryIcon(industry.icon)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (id: IndustryId) => {
    setIndustry(id)
    setIsOpen(false)
    onSelect?.(id)
  }

  // ==========================================================================
  // DROPDOWN VARIANT
  // ==========================================================================

  if (variant === 'dropdown') {
    return (
      <div ref={dropdownRef} className={clsx('relative', className)}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
            'bg-surface-800 border border-surface-700 hover:border-surface-600',
            'text-surface-200 text-sm font-medium',
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin text-primary-400" />
          ) : (
            <CurrentIcon size={18} className="text-primary-400" />
          )}
          <span>{industry.name}</span>
          <ChevronDown
            size={16}
            className={clsx(
              'text-surface-400 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-72 z-50">
            <div className="bg-surface-800 border border-surface-700 rounded-lg shadow-xl overflow-hidden">
              <div className="px-3 py-2 bg-surface-900 border-b border-surface-700">
                <p className="text-xs text-surface-400 font-medium uppercase tracking-wider">
                  Select Industry
                </p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {industries.map(ind => {
                  const Icon = getIndustryIcon(ind.icon)
                  const isSelected = ind.id === industryId

                  return (
                    <button
                      key={ind.id}
                      onClick={() => handleSelect(ind.id)}
                      className={clsx(
                        'w-full px-3 py-3 flex items-start gap-3 transition-colors text-left',
                        isSelected
                          ? 'bg-primary-500/10 border-l-2 border-primary-500'
                          : 'hover:bg-surface-700 border-l-2 border-transparent'
                      )}
                    >
                      <div
                        className={clsx(
                          'p-2 rounded-lg',
                          isSelected ? 'bg-primary-500/20' : 'bg-surface-700'
                        )}
                      >
                        <Icon
                          size={20}
                          className={isSelected ? 'text-primary-400' : 'text-surface-400'}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={clsx(
                              'font-medium',
                              isSelected ? 'text-primary-400' : 'text-surface-200'
                            )}
                          >
                            {ind.name}
                          </span>
                          {isSelected && <Check size={14} className="text-primary-400" />}
                        </div>
                        {showDescription && (
                          <p className="text-xs text-surface-400 mt-0.5 line-clamp-2">
                            {ind.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-surface-500">
                            {ind.assetTypeCount} asset types
                          </span>
                          <span className="text-xs text-surface-500">
                            {ind.kpiDomainCount} KPI domains
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ==========================================================================
  // CARDS VARIANT
  // ==========================================================================

  if (variant === 'cards') {
    return (
      <div className={clsx('grid grid-cols-2 md:grid-cols-3 gap-4', className)}>
        {industries.map(ind => {
          const Icon = getIndustryIcon(ind.icon)
          const isSelected = ind.id === industryId

          return (
            <button
              key={ind.id}
              onClick={() => handleSelect(ind.id)}
              disabled={isLoading}
              className={clsx(
                'p-4 rounded-xl border-2 transition-all text-left',
                isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-surface-700 bg-surface-800 hover:border-surface-600',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={clsx(
                    'p-2 rounded-lg',
                    isSelected ? 'bg-primary-500/20' : 'bg-surface-700'
                  )}
                >
                  <Icon
                    size={24}
                    className={isSelected ? 'text-primary-400' : 'text-surface-400'}
                  />
                </div>
                {isSelected && <Check size={16} className="text-primary-400 ml-auto" />}
              </div>
              <h3
                className={clsx(
                  'font-semibold mb-1',
                  isSelected ? 'text-primary-400' : 'text-surface-200'
                )}
              >
                {ind.name}
              </h3>
              {showDescription && (
                <p className="text-xs text-surface-400 line-clamp-2">{ind.description}</p>
              )}
              <div className="flex items-center gap-2 mt-2 text-xs text-surface-500">
                <span>{ind.assetTypeCount} types</span>
                <span>•</span>
                <span>{ind.kpiDomainCount} KPIs</span>
              </div>
            </button>
          )
        })}
      </div>
    )
  }

  // ==========================================================================
  // MINIMAL VARIANT
  // ==========================================================================

  return (
    <button
      onClick={() => setIsOpen(!isOpen)}
      disabled={isLoading}
      className={clsx(
        'flex items-center gap-1.5 px-2 py-1 rounded transition-colors',
        'text-surface-400 hover:text-surface-200 hover:bg-surface-800',
        'text-xs font-medium',
        className
      )}
    >
      {isLoading ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <CurrentIcon size={14} />
      )}
      <span>{industry.name}</span>
    </button>
  )
}

// =============================================================================
// INDUSTRY BADGE COMPONENT
// =============================================================================

interface IndustryBadgeProps {
  className?: string
  showIcon?: boolean
}

export function IndustryBadge({ className, showIcon = true }: IndustryBadgeProps) {
  const { industry } = useIndustry()
  const Icon = getIndustryIcon(industry.icon)

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full',
        'bg-primary-500/10 text-primary-400 text-xs font-medium',
        className
      )}
    >
      {showIcon && <Icon size={12} />}
      <span>{industry.name}</span>
    </span>
  )
}

// =============================================================================
// EXPORTS
// =============================================================================

export default IndustrySelector
